import { getContrast, hsl, parseToHsl } from 'polished';
import { readableTextColor } from './readableTextColor';

/** WCAG 2.1 minimum contrast ratios for text against the surface behind it. */
export const WCAG_AA_CONTRAST = 4.5;
export const WCAG_AAA_CONTRAST = 7;

/**
 * AAA for large text — 18pt, or 14pt bold, and up. WCAG grants large text a lower bar
 * than body copy, and it coincides with the body-size AA figure; they are separate rows
 * of the same table, so a caller picks the one matching the text it is colouring.
 */
export const WCAG_AAA_LARGE_TEXT_CONTRAST = 4.5;

// Relative luminance moves monotonically with HSL lightness at a fixed hue and
// saturation, so bisection converges on the least-shifted shade that clears the
// ratio. Twelve halvings resolve lightness far finer than an 8-bit channel.
const BISECTION_STEPS = 12;

// Below this saturation a color is a surface — white, near-white, grey — rather
// than a brand accent. Shading one yields an anonymous grey that says nothing
// about the brand, so a later candidate is preferred.
const MIN_BRAND_SATURATION = 0.1;

const contrastAgainst = (color: string, background: string): number => {
  try {
    return getContrast(color, background);
  } catch {
    return 0;
  }
};

const hasBrandHue = (color: string): boolean => {
  try {
    return parseToHsl(color).saturation >= MIN_BRAND_SATURATION;
  } catch {
    return false;
  }
};

/**
 * Moves a color toward black on a light surface, or toward white on a dark one,
 * until it clears the ratio. Hue and saturation are held, so the result still
 * reads as the brand color rather than as an unrelated one.
 *
 * When the ratio is unreachable on this surface the search settles on the extreme
 * — plain black or white — which is the most legible shade available.
 */
function shadeToContrast(color: string, background: string, minContrast: number): string {
  const { hue, saturation, lightness } = parseToHsl(color);
  const shade = (value: number) => hsl({ hue, saturation, lightness: value });
  let failing = lightness;
  let passing = readableTextColor(background) === '#fff' ? 1 : 0;

  for (let step = 0; step < BISECTION_STEPS; step += 1) {
    const midpoint = (failing + passing) / 2;
    if (contrastAgainst(shade(midpoint), background) >= minContrast) {
      passing = midpoint;
    } else {
      failing = midpoint;
    }
  }
  return shade(passing);
}

/**
 * Picks a brand color that is legible as text on `background`, given the brand's
 * own colors in priority order.
 *
 * The first candidate that already clears the ratio is returned untouched, so a
 * brand whose palette is compliant renders exactly as authored. When none does,
 * the first candidate carrying a brand hue is shaded until it clears — keeping
 * the brand rather than substituting a different color. A palette of nothing but
 * greys leaves only plain black or white.
 */
export function accessibleBrandColor(
  candidates: string[],
  background: string,
  minContrast: number = WCAG_AA_CONTRAST,
): string {
  const legible = candidates.find((color) => contrastAgainst(color, background) >= minContrast);
  if (legible) {
    return legible;
  }
  const shadeable = candidates.find(hasBrandHue);
  return shadeable ? shadeToContrast(shadeable, background, minContrast) : readableTextColor(background);
}
